import type { APIEvent, TransportItem } from '@grafana/faro-core';
import {
    ConsoleInstrumentation,
    ErrorsInstrumentation,
    initializeFaro,
    InternalLoggerLevel
} from '@grafana/faro-web-sdk';
import { getDefaultOTELInstrumentations, TracingInstrumentation } from '@grafana/faro-web-tracing';
import routes from '../../routes.json';
import { getMatchingRoutePattern, getPageNameFromRoutePattern } from '../common/utils/routes';
import { SSOLogArgs } from './types';
import {
    buildAuthCorsPatterns,
    buildIgnorePatterns,
    buildLogContext,
    createBusinessRoutesPattern,
    maskEmailForLogging
} from './utils';

const businessRoutesPattern = createBusinessRoutesPattern(routes);
const ignorePatterns = buildIgnorePatterns(businessRoutesPattern);
const authCorsPatterns = buildAuthCorsPatterns();

type CachedPageInfo = {
    pathname: string;
    pageId: string;
    matchedRoutePattern: string | null;
};

let cachedPageInfo: CachedPageInfo = {
    pathname: '',
    pageId: '',
    matchedRoutePattern: null
};

const applyPageMeta = (event: TransportItem<APIEvent>): TransportItem<APIEvent> => {
    const currentPathname = window.location.pathname || '';

    if (currentPathname === cachedPageInfo.pathname && cachedPageInfo.matchedRoutePattern !== null) {
        if (event?.meta?.page) {
            event.meta.page.id = cachedPageInfo.pageId;
        }
        return event;
    }

    const matchedRoutePattern = getMatchingRoutePattern(currentPathname);

    if (!matchedRoutePattern) {
        cachedPageInfo = {
            pathname: currentPathname,
            matchedRoutePattern: null,
            pageId: ''
        };
        return event;
    }

    const newPageId = getPageNameFromRoutePattern(matchedRoutePattern);

    cachedPageInfo = {
        pathname: currentPathname,
        matchedRoutePattern,
        pageId: newPageId
    };

    if (event?.meta?.page) {
        event.meta.page.id = newPageId;
    }

    return event;
};

const version = window._env_.VERSION || 'version-missing';
const namespace = window._env_.NAMESPACE || 'dev';
const isDev = namespace === 'dev';
const isLocalDevOrTestRuntime = ['development', 'test'].includes(process.env.NODE_ENV ?? '');
const isFaroDisabled = isLocalDevOrTestRuntime && String(window._env_['DISABLE_FARO']).toLowerCase() === 'true';

// In non-dev environments (test, preview, prod), only capture errors via ErrorsInstrumentation
// In dev, capture all console logs via ConsoleInstrumentation
const baseInstrumentations: (ConsoleInstrumentation | ErrorsInstrumentation)[] = [new ErrorsInstrumentation()];
if (isDev) {
    baseInstrumentations.unshift(new ConsoleInstrumentation());
}

const FaroClient = isFaroDisabled
    ? ({ api: undefined } as ReturnType<typeof initializeFaro>)
    : initializeFaro({
          url: '/faro',
          app: {
              name: 'main',
              version
          },
          instrumentations: baseInstrumentations,
          internalLoggerLevel: isDev ? InternalLoggerLevel.VERBOSE : InternalLoggerLevel.ERROR,
          batching: {
              enabled: true, // ✅ Enable batching globally
              sendTimeout: 5000, // Batch for 5 seconds (adjust as needed)
              itemLimit: 50 // Or send when 50 events accumulated
          },
          beforeSend: applyPageMeta
      });

const SSOFaroClient = isFaroDisabled
    ? ({ api: undefined } as ReturnType<typeof initializeFaro>)
    : initializeFaro({
          url: '/faro',
          app: {
              name: 'main',
              version
          },
          instrumentations: [
              new ConsoleInstrumentation(),
              new ErrorsInstrumentation(),
              // TracingInstrumentation for SSO/Auth distributed tracing only
              // Note: Traces all auth/SSO calls including external IDPs (Cognito, Okta, etc.)
              // Excludes internal application APIs to focus on authentication flows
              new TracingInstrumentation({
                  instrumentations: getDefaultOTELInstrumentations({
                      // Ignore internal application endpoints, keep all auth/SSO traffic
                      ignoreUrls: ignorePatterns,
                      propagateTraceHeaderCorsUrls: authCorsPatterns
                  })
              })
          ],
          internalLoggerLevel: isDev ? InternalLoggerLevel.VERBOSE : InternalLoggerLevel.ERROR,
          batching: {
              enabled: false, // ✅ Disable batching for SSO events
              sendTimeout: 0, // Batch for 5 seconds (adjust as needed)
              itemLimit: 1 // Or send when 50 events accumulated
          },
          isolate: true,
          preventGlobalExposure: true,
          beforeSend: applyPageMeta
      });

export const logSSOFlow = (logArgs: SSOLogArgs) => {
    try {
        const { stage } = logArgs;
        const sanitizedArgs: SSOLogArgs = {
            ...logArgs,
            success: logArgs.success ?? true,
            email: maskEmailForLogging(logArgs.email),
            metadata: logArgs.metadata ? { ...logArgs.metadata } : undefined
        };
        const context = buildLogContext(sanitizedArgs);

        const message = `SSO-Flow ${stage}`;

        if (SSOFaroClient?.api) {
            SSOFaroClient.api.pushEvent(message, context, undefined, {
                skipDedupe: true
            });
            return;
        }

        FaroClient.api?.pushEvent(message, context, undefined, {
            skipDedupe: true
        });
    } catch (err) {
        console.error('Failed to log SSO-Flow to Faro:', err);
    }
};

export const updateFaroUser = ({ email, accountId, orgId }: { email: string; accountId: string; orgId: string }) => {
    try {
        FaroClient.api?.setUser({
            email,
            id: accountId,
            attributes: {
                orgId
            }
        });
        SSOFaroClient?.api?.setUser({
            email,
            id: accountId,
            attributes: {
                orgId
            }
        });
    } catch (error) {
        console.error('Failed to update Faro user:', error);
    }
};

export default FaroClient;
